import type { GenericReport } from 'bindings/reports/generic/GenericReport';
import * as d3 from 'd3';
import type { ReportColumnType } from '../../../atoms/additionalColumns';
import { Product as AtomProducts } from '../../../atoms/product';
import { OnDateChangeType } from '../../../components_v2/toolbarFilter/time/DatePicker';
import {
	AiGradiant,
	AmberSidely,
	BlueGreySidely,
	PrimaryColor,
	BlushSidely,
	BorderColor,
	BrightRedSidely,
	BrightTealSidely,
	BrightYellowSidely,
	BrownSidely,
	BurntOrangeSidely,
	CeruleanSidely,
	CoolGreySidely,
	CoralSidely,
	DarkGreySidely,
	DarkGreySidely2,
	EmeraldSidely,
	ForestGreenSidely,
	GreenSidely,
	GreySidely3,
	IndigoSidely,
	LavenderSidely,
	LightCyanSidely,
	LightLavenderSidely,
	LightPeachSidely,
	LightPinkSidely,
	LightTealSidely,
	LimeGreenSidely,
	LimeYellowSidely,
	MauveSidely,
	MediumPurpleSidely,
	OliveSidely,
	OrangeSidely,
	PeachSidely,
	PeriwinkleBlueSidely,
	PlumSidely,
	RedSidely,
	SalmonSidely,
	SidelyBlack,
	SiennaSidely,
	SkyBlueSidely,
	SlateSidely,
	SteelBlueSidely,
	SurfaceColor,
	TealSidely,
	YellowGreenSidely,
	YellowSidely
} from '../../../styles/global/css/Utils';
import { translateToString } from '../../../styles/global/translate';
import type { Report } from '../../reports/action';
import { getGenericAndDecodeReport } from '../../reports/generic/action';
import { getFinalyFilteredProducts } from '../../reports/generic/generic';
import type { Report as BareReport, Row, Value } from '../../reports/interpretor/bareReportingDecoder';
import { goalPathHash } from '../../reports/interpretor/ReportInterpretor';
import { isSingleValueChart, isStaticChart } from '../action';
import type { ChartData, Dashboard, DashboardChartConfigWithChartData } from '../action';
import { createBarSvg } from '../charts/Bar';
import { createGaugeDiv } from '../charts/Gauge';
import { createGroupedBarSvg } from '../charts/GroupedBar';
import { createLineSvg } from '../charts/Line';
import { createNumberDiv } from '../charts/Number';
import { createPieSvg } from '../charts/Pie';
import { getGoalsAndGenerateTree, GoalTree } from './goals';

export interface ElementWithCleanup extends Element {
	_cleanupTooltip?: () => void;
}

type ChartSvgResult = { element: SVGSVGElement | HTMLDivElement; cleanup?: () => void };

export type DashboardState = {
	dashboard: Dashboard | null;
	foundationUrl: string;
	authToken: string;
	rawProducts: AtomProducts[];
	users?: Map<number, string>;
	products?: Map<string, string>;
	companies?: Map<number, string>;
	campaigns?: Map<number, string>;
	onCreateChart?: (dashboardUuid: string) => void;
	onEditChart?: (chartUuid: string) => void;
	onSeeReport?: (reportId: number) => void;
	onAiChat?: (chart: DashboardChartConfigWithChartData, data: (ValueNameData | GroupedValueNameData[])[]) => void;
	onChartDataLoad?: (chartUuid: string, data: (ValueNameData | GroupedValueNameData[])[]) => void;
	onRemoveChart?: (dashboardUuid: string, chartUuid: string) => void;
	companyId?: number;
	userId?: number;
	filterDates?: OnDateChangeType;
	onDuplicateChart?: (dashboardUuid: string, chartUuid: string) => void;
	onOpenFullscreen?: (props: GenerateChartSvgProps) => void;
	canExportChart?: boolean;
	locale?: string;
	maxCharts?: number;
	isMobile?: boolean;
};

export const CHART_HEIGHT = 200;
export function chartBackgroundColor(color: string): string {
	return `color-mix(in srgb, ${color} 10%, white)`;
}
export const CHART_MAX_HEIGHT = 300;
const CHART_FONT_CSS = `
	svg text { font-size: 10px !important; }
	svg text.bar-value-label { font-size: 10px !important; }
	svg text.x-axis-legend, svg text.y-axis-legend { font-size: 12px !important; }
	svg text.d3-focus-text { font-size: 11px !important; }
	svg g.legend text { font-size: 11px !important; }
	svg g.d3-legend text { font-size: 12px !important; }
`;
const EXPORT_PADDING = 24;
const EXPORT_TITLE_HEIGHT = 32;
const EXPORT_MAX_EDGE = 4096;
const BAR_MARGIN = 40;
const BAR_MARGIN_LEFT = 100;
const BAR_MARGIN_RIGHT = 20;
const GROUPED_BAR_MARGIN_LEFT = 30;
const PIE_MARGIN_TOPBOT = 15;
const PIE_MARGIN_LEFTRIGHT = 0;
const LINE_MARGIN_TOPBOT = 40;
const LINE_MARGIN_LEFT = 25;
const LINE_MARGIN_RIGHT = 40;
const NUMBER_MARGIN = 10;
const TEXT_FONT_SIZES = { small: '14px', medium: '20px', large: '28px' };

const AllPeriods: string[] = ['Today', 'ThisWeek', '7DaysAgo', 'LastWeek', 'ThisMonth', '30DaysAgo', 'LastMonth', '90DaysAgo', 'ThisYear', '365DaysAgo', 'LastYear'];

function createLoaderElement(width: string = '40px'): HTMLDivElement {
	const loader = document.createElement('div');
	Object.assign(loader.style, {
		width: width,
		height: width,
		position: 'relative',
		borderRadius: '50%',
		border: `calc(${width} / 11) solid`,
		borderColor: `${PrimaryColor} transparent ${PrimaryColor} transparent`,
		animation: 'lds-dual-ring 1.2s linear infinite'
	});

	// Add keyframes if not already present
	if (!document.querySelector('#loader-keyframes')) {
		const style = document.createElement('style');
		style.id = 'loader-keyframes';
		style.textContent = `
			@keyframes lds-dual-ring {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
	}

	return loader;
}

function createCenteredLoader(): HTMLDivElement {
	const container = document.createElement('div');
	Object.assign(container.style, {
		width: '100%',
		height: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '120px'
	});

	const loader = createLoaderElement();
	container.appendChild(loader);
	return container;
}

function chartOptionsDropdown(): SVGSVGElement {
	// Create a simple ellipsis-like SVG (three dots) to use as the "more" icon
	const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
	svg.setAttribute('width', '24');
	svg.setAttribute('height', '24');
	svg.setAttribute('viewBox', '0 0 24 24');
	svg.setAttribute('fill', 'none');
	const createDot = (cx: number) => {
		const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
		c.setAttribute('cx', String(cx));
		c.setAttribute('cy', '12');
		c.setAttribute('r', '2');
		c.setAttribute('fill', GreySidely3);
		return c;
	};
	// Space dots further apart for clearer separation
	svg.appendChild(createDot(5));
	svg.appendChild(createDot(12));
	svg.appendChild(createDot(19));
	return svg;
}

function createFullscreenIcon(): SVGSVGElement {
	const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
	svg.setAttribute('width', '14');
	svg.setAttribute('height', '14');
	svg.setAttribute('viewBox', '0 0 24 24');
	svg.setAttribute('fill', 'none');
	svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

	// First path element (rounded rectangle border)
	const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
	path1.setAttribute('d', 'M2 12C2 7.28595 2 4.92893 3.46447 3.46447C4.92893 2 7.28595 2 12 2C16.714 2 19.0711 2 20.5355 3.46447C22 4.92893 22 7.28595 22 12C22 16.714 22 19.0711 20.5355 20.5355C19.0711 22 16.714 22 12 22C7.28595 22 4.92893 22 3.46447 20.5355C2 19.0711 2 16.714 2 12Z');
	path1.setAttribute('stroke', DarkGreySidely);
	path1.setAttribute('stroke-width', '1.5');

	// Second path element (fullscreen arrows)
	const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
	path2.setAttribute('d', 'M17 7H13M17 7V11M17 7L13.5 10.5M7 17H11M7 17V13M7 17L10.5 13.5');
	path2.setAttribute('stroke', DarkGreySidely);
	path2.setAttribute('stroke-width', '1.5');
	path2.setAttribute('stroke-linecap', 'round');
	path2.setAttribute('stroke-linejoin', 'round');

	svg.appendChild(path1);
	svg.appendChild(path2);

	return svg;
}

function createExternalLinkIcon(): SVGSVGElement {
	// Create external link icon SVG
	const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
	svg.setAttribute('width', '14');
	svg.setAttribute('height', '14');
	svg.setAttribute('viewBox', '0 0 24 24');
	svg.setAttribute('fill', 'none');
	svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

	// First path element
	const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
	path1.setAttribute('d', 'M13 11L22 2M22 2H16.6562M22 2V7.34375');
	path1.setAttribute('stroke', DarkGreySidely);
	path1.setAttribute('stroke-width', '1.5');
	path1.setAttribute('stroke-linecap', 'round');
	path1.setAttribute('stroke-linejoin', 'round');

	// Second path element
	const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
	path2.setAttribute('d', 'M22 12C22 16.714 22 19.0711 20.5355 20.5355C19.0711 22 16.714 22 12 22C7.28595 22 4.92893 22 3.46447 20.5355C2 19.0711 2 16.714 2 12C2 7.28595 2 4.92893 3.46447 3.46447C4.92893 2 7.28595 2 12 2');
	path2.setAttribute('stroke', DarkGreySidely);
	path2.setAttribute('stroke-width', '1.5');
	path2.setAttribute('stroke-linecap', 'round');

	svg.appendChild(path1);
	svg.appendChild(path2);

	return svg;
}

function createAiSparklingSvg(): SVGSVGElement {
	const svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
	svgElement.setAttribute('width', '24');
	svgElement.setAttribute('height', '24');
	svgElement.setAttribute('viewBox', '0 0 24 24');
	svgElement.setAttribute('fill', 'none');

	const pathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');
	pathElement.setAttribute('d', 'M9.72524 5.81511C10.2135 4.38633 12.1877 4.34306 12.7665 5.6853L12.8155 5.81593L13.4744 7.74275C13.6254 8.18464 13.8694 8.589 14.1899 8.92858C14.5105 9.26815 14.9001 9.53502 15.3326 9.7112L15.5098 9.77734L17.4366 10.4354C18.8654 10.9236 18.9087 12.8978 17.5672 13.4767L17.4366 13.5257L15.5098 14.1845C15.0677 14.3354 14.6632 14.5794 14.3235 14.8999C13.9838 15.2205 13.7168 15.6102 13.5405 16.0428L13.4744 16.2191L12.8163 18.1467C12.3281 19.5755 10.3539 19.6188 9.77586 18.2774L9.72524 18.1467L9.06719 16.2199C8.91629 15.7779 8.67233 15.3734 8.35177 15.0336C8.0312 14.6939 7.6415 14.4269 7.20895 14.2507L7.0326 14.1845L5.10578 13.5265C3.67618 13.0382 3.63291 11.0641 4.97515 10.486L5.10578 10.4354L7.0326 9.77734C7.47449 9.62634 7.87885 9.38233 8.21843 9.06178C8.558 8.74122 8.82487 8.35157 9.00105 7.9191L9.06719 7.74275L9.72524 5.81511ZM11.2708 6.34254L10.6127 8.26936C10.3828 8.94317 10.0088 9.55878 9.51667 10.0733C9.02457 10.5878 8.42623 10.9889 7.76332 11.2486L7.55921 11.3229L5.63239 11.9809L7.55921 12.639C8.23302 12.8689 8.84863 13.2429 9.36315 13.735C9.87767 14.2271 10.2787 14.8255 10.5384 15.4884L10.6127 15.6925L11.2708 17.6193L11.9288 15.6925C12.1588 15.0187 12.5328 14.4031 13.0249 13.8886C13.517 13.374 14.1153 12.973 14.7782 12.7133L14.9824 12.6398L16.9092 11.9809L14.9824 11.3229C14.3085 11.093 13.6929 10.7189 13.1784 10.2268C12.6639 9.73472 12.2628 9.13638 12.0031 8.47347L11.9297 8.26936L11.2708 6.34254ZM17.8024 3C17.9551 3 18.1048 3.04285 18.2344 3.12367C18.364 3.20449 18.4683 3.32005 18.5355 3.45721L18.5747 3.55274L18.8605 4.39041L19.699 4.67617C19.852 4.72817 19.9862 4.82445 20.0845 4.95279C20.1828 5.08114 20.2408 5.23578 20.2511 5.39711C20.2614 5.55844 20.2236 5.71921 20.1425 5.85903C20.0613 5.99885 19.9405 6.11143 19.7953 6.18252L19.699 6.2217L18.8613 6.50746L18.5755 7.34595C18.5234 7.49898 18.4271 7.6331 18.2987 7.73131C18.1703 7.82953 18.0157 7.88742 17.8543 7.89765C17.693 7.90788 17.5323 7.86999 17.3925 7.78878C17.2527 7.70757 17.1402 7.5867 17.0692 7.44148L17.03 7.34595L16.7443 6.50828L15.9058 6.22252C15.7527 6.17052 15.6185 6.07424 15.5202 5.9459C15.4219 5.81755 15.3639 5.66291 15.3536 5.50158C15.3433 5.34025 15.3811 5.17948 15.4622 5.03966C15.5434 4.89984 15.6642 4.78725 15.8094 4.71617L15.9058 4.67698L16.7434 4.39123L17.0292 3.55274C17.0842 3.39143 17.1884 3.25139 17.3271 3.15227C17.4657 3.05314 17.6319 2.9999 17.8024 3Z');
	pathElement.setAttribute('fill', 'white');

	svgElement.appendChild(pathElement);
	return svgElement;
}

function createLinkTooltip(text: string): HTMLDivElement {
	const tooltip = document.createElement('div');
	tooltip.className = 'link-tooltip';
	tooltip.textContent = text;
	Object.assign(tooltip.style, {
		position: 'absolute',
		background: SidelyBlack,
		color: 'white',
		padding: '6px 8px',
		borderRadius: '4px',
		fontSize: '12px',
		whiteSpace: 'nowrap',
		top: '-35px',
		left: '50%',
		transform: 'translateX(-50%)',
		opacity: '0',
		visibility: 'hidden',
		transition: 'opacity 0.2s ease, visibility 0.2s ease',
		zIndex: '1000',
		pointerEvents: 'none'
	});
	return tooltip;
}

export type GenerateChartSvgProps = {
	report: BareReport,
	chart: DashboardChartConfigWithChartData,
	state: DashboardState,
	width: number,
	goalTree: GoalTree | undefined,
	setData: ((data: (ValueNameData | GroupedValueNameData[])[]) => void) | undefined,
	height: number | undefined,
	onNavigateInData: ((svg: SVGSVGElement | HTMLDivElement | null, breadCrumb: HTMLDivElement | null) => void) | undefined,
	filterPath: InnerValue[],
};

function computeChartData(report: BareReport, chart: DashboardChartConfigWithChartData, state: DashboardState, goalTree: GoalTree | undefined, filterPath: InnerValue[]): (ValueNameData | GroupedValueNameData[])[] {
	const reportHeaders = report.panels[chart.data.panel_index].headers;
	const nonPrimaryHeaders = reportHeaders.filter(h => !h.primary);
	const headers = getChartHeadersFromReport(report, chart.data);
	const rawData = goalTree ? getChartDataFromGoalTree(goalTree, chart.data, filterPath) : getChartDataFromReport(report, chart.data, filterPath);
	return prepareChartDataFromRaw(rawData, headers, chart, state, filterPath, report, reportHeaders.length - nonPrimaryHeaders.length);
}

// Fetches and computes a chart's data without rendering it
export async function fetchChartData(chart: DashboardChartConfigWithChartData, state: DashboardState): Promise<(ValueNameData | GroupedValueNameData[])[]> {
	if (chart.report_id == null) return [];
	const { report, definition } = await getReport({
		companyId: state.companyId,
		userId: state.userId,
		filterDates: state.filterDates,
		products: state.rawProducts,
		foundationUrl: state.foundationUrl,
		bearerToken: state.authToken,
		id: chart.report_id
	});
	const goalTree = await getGoalsAndGenerateTree(chart, definition, report, state);
	return computeChartData(report, chart, state, goalTree, []);
}

export function createStaticWidget(chart: DashboardChartConfigWithChartData, height?: number): HTMLDivElement {
	const opts = chart.data.options;
	const margins = opts?.margins;
	const container = document.createElement('div');
	Object.assign(container.style, {
		width: '100%',
		height: height != null ? `${height}px` : '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		boxSizing: 'border-box',
		padding: `${margins?.top ?? 0}px ${margins?.right ?? 0}px ${margins?.bottom ?? 0}px ${margins?.left ?? 0}px`,
	});
	if (chart.representation === 'image') {
		if (!opts?.image) {
			container.textContent = translateToString('dashboard.no_image');
			return container;
		}
		const frame = document.createElement('div');
		Object.assign(frame.style, {
			position: 'relative',
			width: '100%',
			height: '100%',
			minHeight: '0',
		});
		const img = document.createElement('img');
		img.src = opts.image;
		img.alt = chart.name ?? '';
		Object.assign(img.style, {
			position: 'absolute',
			inset: '0',
			width: '100%',
			height: '100%',
			objectFit: opts.image_fit ?? 'contain',
			borderRadius: '6px',
		});
		frame.appendChild(img);
		container.appendChild(frame);
		return container;
	}
	const text = document.createElement('div');
	text.textContent = opts?.text ?? '';
	Object.assign(text.style, {
		width: '100%',
		textAlign: opts?.text_align ?? 'left',
		fontSize: TEXT_FONT_SIZES[opts?.text_size ?? 'medium'],
		fontWeight: '600',
		lineHeight: '1.3',
		whiteSpace: 'pre-wrap',
		overflowWrap: 'anywhere',
	});
	container.appendChild(text);
	return container;
}

export function generateChartSvg({
	report,
	chart,
	state,
	width,
	goalTree,
	setData,
	height,
	onNavigateInData,
	filterPath,
}: GenerateChartSvgProps): ChartSvgResult | null {
	const reportHeaders = report.panels[chart.data.panel_index].headers;
	const primaryHeaders = reportHeaders.filter(h => h.primary);
	const headers = getChartHeadersFromReport(report, chart.data);
	const data = computeChartData(report, chart, state, goalTree, filterPath);
	setData?.(data);

	const cleanup: { current: ChartSvgResult['cleanup'] | null } = { current: null };
	const chartHeight = height ? height : chart.data.options?.row_span != null ? CHART_HEIGHT * chart.data.options.row_span : CHART_HEIGHT;
	function generateBreadCrumb(filterPath: InnerValue[]): HTMLDivElement | null {
		if (filterPath.length === 0 || !onNavigateInData) return null;
		const breadCrumb = document.createElement('div');
		Object.assign(breadCrumb.style, {
			display: 'flex',
			alignItems: 'center',
			gap: '8px',
			marginBottom: '8px',
			fontSize: '12px',
			color: DarkGreySidely2,
			cursor: 'pointer',
		});

		filterPath.forEach((value, index) => {
			const crumb = document.createElement('span');
			crumb.textContent = valueInnerToString(value, state) ?? '';
			Object.assign(crumb.style, {
				textDecoration: 'underline',
			});

			crumb.onclick = () => {
				const newFilterPath = filterPath.slice(0, index);
				cleanup.current?.();
				const svg = generateChartSvg({ report, chart, state, width, goalTree, setData, height, onNavigateInData, filterPath: newFilterPath });
				onNavigateInData(svg?.element ?? null, generateBreadCrumb(newFilterPath));
			};

			breadCrumb!.appendChild(crumb);

			if (index < filterPath.length - 1) {
				const separator = document.createElement('span');
				separator.textContent = '>';
				breadCrumb!.appendChild(separator);
			}
		});
		return breadCrumb;
	}

	function createBarChartIcon(): SVGSVGElement {
		const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
		svg.setAttribute('width', '48');
		svg.setAttribute('height', '48');
		svg.setAttribute('viewBox', '0 0 24 24');
		svg.setAttribute('fill', 'none');

		// Main bar
		const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
		path1.setAttribute('fill-rule', 'evenodd');
		path1.setAttribute('clip-rule', 'evenodd');
		path1.setAttribute('d', 'M14 20.5V4.25C14 3.52169 13.9984 3.05091 13.9518 2.70403C13.908 2.37872 13.8374 2.27676 13.7803 2.21967C13.7232 2.16258 13.6213 2.09197 13.296 2.04823C12.9491 2.00159 12.4783 2 11.75 2C11.0217 2 10.5509 2.00159 10.204 2.04823C9.87872 2.09197 9.77676 2.16258 9.71967 2.21967C9.66258 2.27676 9.59197 2.37872 9.54823 2.70403C9.50159 3.05091 9.5 3.52169 9.5 4.25V20.5H14Z');
		path1.setAttribute('fill', GreySidely3);

		// Left bar
		const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
		path2.setAttribute('opacity', '0.7');
		path2.setAttribute('d', 'M8 8.75C8 8.33579 7.66421 8 7.25 8H4.25C3.83579 8 3.5 8.33579 3.5 8.75V20.5H8V8.75Z');
		path2.setAttribute('fill', GreySidely3);

		// Right bar
		const path3 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
		path3.setAttribute('opacity', '0.7');
		path3.setAttribute('d', 'M20 13.75C20 13.3358 19.6642 13 19.25 13H16.25C15.8358 13 15.5 13.3358 15.5 13.75V20.5H20V13.75Z');
		path3.setAttribute('fill', GreySidely3);

		// Base line
		const path4 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
		path4.setAttribute('opacity', '0.5');
		path4.setAttribute('d', 'M1.75 20.5C1.33579 20.5 1 20.8358 1 21.25C1 21.6642 1.33579 22 1.75 22H21.75C22.1642 22 22.5 21.6642 22.5 21.25C22.5 20.8358 22.1642 20.5 21.75 20.5H21.5H20H15.5H14H9.5H8H3.5H2H1.75Z');
		path4.setAttribute('fill', GreySidely3);

		svg.appendChild(path1);
		svg.appendChild(path2);
		svg.appendChild(path3);
		svg.appendChild(path4);

		return svg;
	}

	function createNoDataDiv(): HTMLDivElement {
		const noDataDiv = document.createElement('div');
		Object.assign(noDataDiv.style, {
			width: '100%',
			height: '100%',
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center',
			fontSize: '14px',
			color: DarkGreySidely2,
			gap: '12px'
		});

		const iconContainer = document.createElement('div');
		const barChartIcon = createBarChartIcon();
		iconContainer.appendChild(barChartIcon);

		const textElement = document.createElement('div');
		textElement.textContent = translateToString('dashboard.no_data');

		noDataDiv.appendChild(iconContainer);
		noDataDiv.appendChild(textElement);

		return noDataDiv;
	}

	const onValueClick = filterPath.length >= primaryHeaders.length - 1 - (chart.data.group_by_index ?? 0) ? undefined : (value: InnerValue) => {
		if (onNavigateInData) {
			const newFilterPath = [...filterPath, value];
			cleanup.current?.();
			const svg = generateChartSvg({ report, chart, state, width, goalTree, setData, height, onNavigateInData, filterPath: newFilterPath });
			onNavigateInData(svg?.element ?? null, generateBreadCrumb(newFilterPath));
		}
	};


	let svg: SVGSVGElement | HTMLDivElement | null = null;
	if (data.length === 0) {
		svg = createNoDataDiv();
		return { element: svg };
	}
	const marginTopOffsetLegend = chart.data.options?.legend !== false && chart.data.options?.legend_orientation !== 'vertical' ? 20 : 0;
	switch (chart.representation) {
		case 'pie':
			svg = createPieSvg({
				chartData: data as ValueNameData[],
				width: width,
				height: chartHeight - 60,
				marginLeftRight: PIE_MARGIN_LEFTRIGHT + (chart.data.options?.margins?.left ?? 0) + (chart.data.options?.margins?.right ?? 0),
				marginTopBottom: PIE_MARGIN_TOPBOT + (chart.data.options?.margins?.top ?? 0) + (chart.data.options?.margins?.bottom ?? 0),
				tooltipId: chart.uuid,
				opts: {
					locale: state.locale,
					number_extension: chart.data.options?.number_extension,
					max_values: chart.data.options?.max_values,
					digits_after_decimal: chart.data.options?.digits_after_decimal,
					legend: chart.data.options?.legend != null ? chart.data.options.legend : true,
					legendItemSpacing: chart.data.options?.legend_item_spacing,
					max_legend_items: chart.data.options?.max_legend_items ?? 8,
					legend_orientation: chart.data.options?.legend_orientation,
					hide_value_labels: chart.data.options?.hide_value_labels ?? false,
					doughnut: chart.data.options?.doughnut ?? false,
					trim_x_labels: chart.data.options?.trim_x_labels,
					distance_from_center: chart.data.options?.distance_from_center,
					min_slice_angle: chart.data.options?.min_slice_angle != null ? chart.data.options.min_slice_angle / 100 : undefined,
				},
				onValueClick
			});
			break;
		case 'bar':
			if (headers && headers.length > 1)
				svg = createGroupedBarSvg({
					chartData: data as GroupedValueNameData[],
					width: width,
					marginTop: BAR_MARGIN + marginTopOffsetLegend + (chart.data.options?.margins?.top ?? 0),
					height: chartHeight,
					marginBottom: BAR_MARGIN + (chart.data.options?.margins?.bottom ?? 0),
					marginLeft: GROUPED_BAR_MARGIN_LEFT + (chart.data.options?.margins?.left ?? 0),
					marginRight: BAR_MARGIN + (chart.data.options?.margins?.right ?? 0),
					granularity: chart.data.options?.granularity,
					opts: {
						legend: chart.data.options?.legend != null ? chart.data.options.legend : true,
						orientation: chart.data.options?.orientation ?? 'vertical',
						sort: chart.data.options?.sort ?? 'none',
						number_extension: chart.data.options?.number_extension,
						digits_after_decimal: chart.data.options?.digits_after_decimal,
						legend_orientation: chart.data.options?.legend_orientation,
						max_legend_items: chart.data.options?.max_legend_items,
						stacked: chart.data.options?.stacked ?? false,
						trim_y_labels: chart.data.options?.trim_y_labels,
						trim_x_labels: chart.data.options?.trim_x_labels,
						x_axis_legend: chart.data.options?.x_axis_legend,
						y_axis_legend: chart.data.options?.y_axis_legend,
						x_axis_boundaries: chart.data.options?.x_axis_boundaries,
						y_axis_boundaries: chart.data.options?.y_axis_boundaries,
						locale: state.locale,
					},
					tooltipId: chart.uuid
				});
			else
				svg = createBarSvg({
					chartData: data as ValueNameData[],
					width: width,
					marginTop: BAR_MARGIN + (chart.data.options?.margins?.top ?? 0),
					height: chartHeight,
					marginBottom: BAR_MARGIN + (chart.data.options?.margins?.bottom ?? 0),
					marginLeft: chart.data.options?.orientation === 'vertical' ? (BAR_MARGIN_RIGHT + (chart.data.options?.margins?.left ?? 0)) : (BAR_MARGIN_LEFT + (chart.data.options?.margins?.left ?? 0)),
					marginRight: BAR_MARGIN_RIGHT + (chart.data.options?.margins?.right ?? 0),
					opts: {
						orientation: chart.data.options?.orientation ?? 'horizontal',
						sort: chart.data.options?.sort ?? 'desc',
						number_extension: chart.data.options?.number_extension,
						max_values: chart.data.options?.max_values,
						digits_after_decimal: chart.data.options?.digits_after_decimal,
						goals: chart.data.goals,
						trim_y_labels: chart.data.options?.trim_y_labels,
						trim_x_labels: chart.data.options?.trim_x_labels,
						x_axis_legend: chart.data.options?.x_axis_legend,
						y_axis_legend: chart.data.options?.y_axis_legend,
						x_axis_boundaries: chart.data.options?.x_axis_boundaries,
						y_axis_boundaries: chart.data.options?.y_axis_boundaries,
						locale: state.locale,
					},
					tooltipId: chart.uuid,
					onValueClick
				});
			break;
		case 'line':
			svg = createLineSvg({
				chartData: data as GroupedValueNameData[],
				granularity: chart.data.options?.granularity,
				width: width,
				height: chartHeight,
				marginTop: LINE_MARGIN_TOPBOT + marginTopOffsetLegend + (chart.data.options?.margins?.top ?? 0),
				marginBottom: LINE_MARGIN_TOPBOT + (chart.data.options?.margins?.bottom ?? 0),
				marginLeft: LINE_MARGIN_LEFT + (chart.data.options?.margins?.left ?? 0),
				marginRight: LINE_MARGIN_RIGHT + (chart.data.options?.margins?.right ?? 0),
				opts: {
					legend: chart.data.options?.legend != null ? chart.data.options.legend : true,
					legendItemSpacing: 120,
					number_extension: chart.data.options?.number_extension,
					colorGradient: chart.data.options?.color_gradient ?? false,
					digits_after_decimal: chart.data.options?.digits_after_decimal,
					legend_orientation: chart.data.options?.legend_orientation,
					max_legend_items: chart.data.options?.max_legend_items,
					trim_y_labels: chart.data.options?.trim_y_labels,
					trim_x_labels: chart.data.options?.trim_x_labels,
					x_axis_legend: chart.data.options?.x_axis_legend,
					y_axis_legend: chart.data.options?.y_axis_legend,
					x_axis_boundaries: chart.data.options?.x_axis_boundaries,
					y_axis_boundaries: chart.data.options?.y_axis_boundaries,
					locale: state.locale,
				},
				tooltipId: chart.uuid
			});
			break;
		case 'number': {
			const numberResult = createNumberDiv({
				chartData: (data.length === 1 ? data[0] : data) as ValueNameData | GroupedValueNameData[],
				width: width,
				height: chart.data.options?.display_line_chart_evolution ? 300 : 100,
				margin: NUMBER_MARGIN,
				granularity: chart.data.options?.granularity,
				opts: {
					locale: state.locale,
					number_extension: chart.data.options?.number_extension,
					digits_after_decimal: chart.data.options?.digits_after_decimal,
					display_line_chart_evolution: chart.data.options?.display_line_chart_evolution ?? false,
					is_percentage: chart.data.options?.is_percentage ?? false,
				},
				tooltipId: chart.uuid
			});
			if (!numberResult) return null;
			return { element: numberResult.container, cleanup: numberResult.cleanup };
		}
		case 'gauge': {
			const gaugeResult = createGaugeDiv({
				chartData: (data.length === 1 ? data[0] : data) as ValueNameData | GroupedValueNameData[],
				width: width,
				height: chartHeight,
				margin: NUMBER_MARGIN,
				opts: {
					locale: state.locale,
					number_extension: chart.data.options?.number_extension,
					digits_after_decimal: chart.data.options?.digits_after_decimal,
					min: chart.data.options?.gauge_boundaries?.min,
					max: chart.data.options?.gauge_boundaries?.max,
				},
				tooltipId: chart.uuid
			});
			if (!gaugeResult) return null;
			return { element: gaugeResult.container, cleanup: gaugeResult.cleanup };
		}
		default:
			return null;
	}
	if (!svg) return null;

	// Extract cleanup function if it exists (for SVG charts)
	cleanup.current = (svg as ElementWithCleanup)._cleanupTooltip;
	return { element: svg, cleanup: cleanup.current };
}

let embeddedFontCss: Promise<string> | null = null;

function blobToDataUrl(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(blob);
	});
}

// An SVG rasterized through <img> cannot use the page's web fonts, so the Inter faces are inlined as data URLs
function getEmbeddedFontCss(): Promise<string> {
	if (embeddedFontCss) return embeddedFontCss;
	embeddedFontCss = (async() => {
		const link = document.querySelector<HTMLLinkElement>('link[href*="fonts.googleapis.com"]');
		if (!link) return '';
		const css = await fetch(link.href).then(r => r.text());
		const faces = css.match(/@font-face\s*{[^}]*}/g) ?? [];
		const interFaces = faces.filter(f => /font-family:\s*'?Inter'?/.test(f) && !/font-style:\s*italic/.test(f));
		const inlined = await Promise.all(interFaces.map(async face => {
			const url = face.match(/url\(([^)]+)\)/)?.[1];
			if (!url) return '';
			const blob = await fetch(url).then(r => r.blob());
			return face.replace(url, await blobToDataUrl(blob));
		}));
		return inlined.join('\n');
	})().catch(() => '');
	return embeddedFontCss;
}

function loadImage(src: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error('svg rasterization failed'));
		img.src = src;
	});
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
	return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('png encoding failed')), 'image/png'));
}

export async function exportChartAsPng(svg: SVGSVGElement, title: string | undefined): Promise<void> {
	const rect = svg.getBoundingClientRect();
	const width = rect.width || Number(svg.getAttribute('width')) || 600;
	const height = rect.height || Number(svg.getAttribute('height')) || CHART_HEIGHT;

	const clone = svg.cloneNode(true) as SVGSVGElement;
	clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
	clone.setAttribute('width', String(width));
	clone.setAttribute('height', String(height));
	const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
	style.textContent = `${await getEmbeddedFontCss()}\nsvg { font-family: Inter, sans-serif; }\n${CHART_FONT_CSS}`;
	clone.insertBefore(style, clone.firstChild);

	const svgUrl = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' }));
	try {
		const img = await loadImage(svgUrl);
		const titleHeight = title ? EXPORT_TITLE_HEIGHT : 0;
		const outWidth = width + EXPORT_PADDING * 2;
		const outHeight = height + EXPORT_PADDING * 2 + titleHeight;
		// Edge cap keeps Safari within its canvas size limits
		const scale = Math.max(1, Math.min(Math.max(4, (window.devicePixelRatio || 1) * 2), EXPORT_MAX_EDGE / Math.max(outWidth, outHeight)));
		const canvas = document.createElement('canvas');
		canvas.width = Math.round(outWidth * scale);
		canvas.height = Math.round(outHeight * scale);
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			URL.revokeObjectURL(svgUrl);
			return;
		}
		ctx.scale(scale, scale);
		ctx.fillStyle = SurfaceColor;
		ctx.fillRect(0, 0, outWidth, outHeight);
		if (title) {
			ctx.fillStyle = '#323C47';
			ctx.font = '600 14px Inter, sans-serif';
			ctx.textBaseline = 'middle';
			ctx.fillText(title, EXPORT_PADDING, EXPORT_PADDING + titleHeight / 2, width);
		}
		ctx.drawImage(img, EXPORT_PADDING, EXPORT_PADDING + titleHeight, width, height);
		const blob = await canvasToBlob(canvas);
		const pngUrl = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = pngUrl;
		link.download = `${(title ?? 'chart').replace(/[\\/:*?"<>|]/g, '_')}.png`;
		document.body.appendChild(link);
		link.click();
		link.remove();
		// Revoking synchronously makes Safari download an empty file
		setTimeout(() => URL.revokeObjectURL(pngUrl), 1000);
	} finally {
		URL.revokeObjectURL(svgUrl);
	}
}

function createChartMenuButton(chart: DashboardChartConfigWithChartData, state: DashboardState, chartWrapper: Element): HTMLDivElement | null {
	const editBtn = document.createElement('div');
	editBtn.className = 'chart-menu-button';
	Object.assign(editBtn.style, {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		cursor: 'pointer',
		borderRadius: '6px',
		transition: 'background-color 0.15s ease',
	});
	editBtn.setAttribute('role', 'button');
	editBtn.tabIndex = 0;
	editBtn.title = 'Chart options';

	const editDropdown = chartOptionsDropdown();
	editDropdown.style.width = '18px';
	editDropdown.style.height = '18px';
	editBtn.appendChild(editDropdown);

	const menu = document.createElement('div');
	menu.className = 'chart-options-dropdown';
	menu.setAttribute('role', 'menu');

	// Helper function to create menu items
	const createMenuItem = (text: string, onClick: () => void) => {
		const menuItem = document.createElement('div');
		menuItem.textContent = text;
		menuItem.className = 'chart-options-dropdown-item';
		menuItem.tabIndex = 0;
		menuItem.setAttribute('role', 'menuitem');

		menuItem.onclick = (e => {
			e.stopPropagation();
			onClick();
			menu.style.display = 'none';
		});
		return menuItem;
	};

	let hasMenuItems = false;

	// Edit chart option (only show if handler is available)
	if (state.onEditChart) {
		const editOption = createMenuItem(translateToString('dashboard.edit_chart'), () => {
			state.onEditChart?.(chart.uuid);
		});
		menu.appendChild(editOption);
		hasMenuItems = true;
	}

	if (state.onRemoveChart) {
		const removeOption = createMenuItem(translateToString('dashboard.remove_chart'), () => {
			state.onRemoveChart?.(state.dashboard!.uuid, chart.uuid);
		});
		menu.appendChild(removeOption);
		hasMenuItems = true;
	}

	if (state.onDuplicateChart && state.dashboard?.charts.length && (state.maxCharts ? state.dashboard?.charts.length < state.maxCharts : true)) {
		const duplicateOption = createMenuItem(translateToString('dashboard.duplicate_chart'), () => {
			state.onDuplicateChart?.(state.dashboard!.uuid, chart.uuid);
		});
		menu.appendChild(duplicateOption);
		hasMenuItems = true;
	}

	// Number charts are plain divs, there is no SVG to rasterize
	if (state.canExportChart && chart.representation !== 'number' && !isStaticChart(chart.representation)) {
		const exportOption = createMenuItem(translateToString('dashboard.export_chart'), () => {
			const svg = chartWrapper.querySelector('.chart-svg-container svg');
			if (svg instanceof SVGSVGElement) exportChartAsPng(svg, chart.name).catch(() => undefined);
		});
		menu.appendChild(exportOption);
		hasMenuItems = true;
	}

	// Only show menu button if there are menu items
	if (!hasMenuItems) {
		return null;
	}

	// Create container for button and menu
	const menuContainer = document.createElement('div');
	menuContainer.appendChild(editBtn);
	menuContainer.appendChild(menu);

	let menuVisible = false;
	const showMenu = () => {
		menu.style.display = 'block';
		menuVisible = true;
		// close on outside click
		setTimeout(() => {
			document.addEventListener('click', onDocClick);
		}, 0);
	};
	const hideMenu = () => {
		menu.style.display = 'none';
		menuVisible = false;
		document.removeEventListener('click', onDocClick);
	};
	const onDocClick = (e: Event) => {
		if (!menu.contains(e.target as Node) && !editBtn.contains(e.target as Node)) {
			hideMenu();
		}
	};

	editBtn.addEventListener('click', (e) => {
		e.stopPropagation();
		if (menuVisible) hideMenu(); else showMenu();
	});

	return menuContainer;
}

export function init(state: DashboardState) {
	const dashboardCtn = document.getElementById('dashboard');
	if (!dashboardCtn || !state.dashboard) return;

	// Clean up any existing tooltip event listeners
	const existingElements = dashboardCtn.querySelectorAll('*');
	existingElements.forEach(element => {
		const cleanupFn = (element as ElementWithCleanup)._cleanupTooltip;
		if (typeof cleanupFn === 'function') {
			try {
				cleanupFn();
			} catch (_e) {
				// Ignore cleanup errors
			}
		}
	});

	dashboardCtn.innerHTML = '';
	const container = document.createElement('div');
	dashboardCtn.appendChild(container);
	container.style.display = 'flex';
	container.style.flexDirection = 'column';
	container.style.gap = '16px';
	container.style.width = '100%';
	container.style.height = '100%';

	const dashboardDiv = document.createElement('div');
	dashboardDiv.className = 'dashboard';
	container.appendChild(dashboardDiv);

	// Add CSS hover rule for the tooltip once
	if (!dashboardCtn.querySelector('#link-tooltip-styles')) {
		const style = document.createElement('style');
		style.id = 'link-tooltip-styles';
		style.textContent = `
			[role="button"]:hover .link-tooltip {
				opacity: 1 !important;
				visibility: visible !important;
			}
		`;
		dashboardCtn.appendChild(style);
	}

	// Add CSS rule to prevent font scaling in SVG charts
	if (!dashboardCtn.querySelector('#chart-font-styles')) {
		const style = document.createElement('style');
		style.id = 'chart-font-styles';
		style.textContent = `${CHART_FONT_CSS}
			.number-chart-value { font-size: 33px !important; }
		`;
		dashboardCtn.appendChild(style);
	}

	if (Array.isArray(state.dashboard.charts)) {
		state.dashboard.charts.forEach(_ => {
			const chartWrapper = document.createElement('div');
			chartWrapper.className = 'card';
			dashboardDiv.appendChild(chartWrapper);
		});
		if (state.onCreateChart) {
			const addChartCard = document.createElement('div');
			addChartCard.className = 'card';
			const addChartContent = document.createElement('div');
			addChartContent.textContent = '+';
			Object.assign(addChartContent.style, {
				fontSize: '40px',
				backgroundColor: PrimaryColor,
				color: 'white',
				width: '40px',
				height: '40px',
				display: 'flex',
				justifyContent: 'center',
				borderRadius: '50%',
				cursor: 'pointer',
				textAlign: 'center',
				lineHeight: '35px',
			});
			addChartCard.appendChild(addChartContent);
			addChartCard.style.gridRow = 'span 2';

			// Trigger creation callback when the + is clicked (also keyboard accessible)
			addChartContent.addEventListener('click', () => {
				try {
					state.onCreateChart?.(state.dashboard!.uuid);
				} catch (e) {
					console.error('onCreateChart handler threw:', e);
				}
			});
			Object.assign(addChartCard.style, {
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				border: `2px dashed ${BorderColor}`
			});
			dashboardDiv.appendChild(addChartCard);
		}
		state.dashboard.charts.sort((a, b) => a.sequence - b.sequence).forEach((chart, i) => {
			const chartWrapper = dashboardDiv.children[i];
			try {
				const row_span = (chart?.data?.options && typeof chart.data.options.row_span === 'number' && Number.isFinite(chart.data.options.row_span) && chart.data.options.row_span > 0) ? chart.data.options.row_span : 1;
				(chartWrapper as HTMLElement).style.gridRow = `span ${Math.max(1, Math.round(row_span * 2))}`;
				const col_span = (chart?.data?.options && typeof chart.data.options.column_span === 'number') ? chart.data.options.column_span : undefined;
				if (col_span != null && Number.isFinite(col_span) && col_span > 0) {
					(chartWrapper as HTMLElement).style.gridColumn = `span ${col_span}`;
				}
			} catch (_) {
				// ignore malformed chart options
			}
			chartWrapper.classList.add(`card-${chart.representation}`);
			if (chart.data?.options?.background_color) (chartWrapper as HTMLElement).style.backgroundColor = chartBackgroundColor(chart.data.options.background_color);
			const isStatic = isStaticChart(chart.representation);
			// Ensure we can position the edit button at the top-right
			(chartWrapper as HTMLElement).style.position = (chartWrapper as HTMLElement).style.position || 'relative';
			let aiButton: HTMLButtonElement | null = null;

			// Add menu button if any handlers are available
			const topRightContainer = document.createElement('div');
			Object.assign(topRightContainer.style, {
				position: 'absolute',
				top: '12px',
				right: '12px',
				zIndex: '1',
				display: 'flex',
				alignItems: 'center',
				gap: '4px',
			});
			// If an edit handler is provided, show a pen icon in the top-right corner
			if (state.onAiChat && !isStatic) {
				aiButton = document.createElement('button');
				Object.assign(aiButton.style, {
					width: '24px',
					height: '24px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					cursor: 'pointer',
					borderRadius: '5px',
					background: AiGradiant,
					border: 'none',
					outline: 'none',
					opacity: state.isMobile ? '1' : '0', // Initially hidden
					transition: 'opacity 0.2s ease-in-out', // Smooth transition
				});

				const aiIcon = createAiSparklingSvg();
				aiIcon.style.width = '24px';
				aiIcon.style.height = '24px';
				aiButton.appendChild(aiIcon);

				if (!state.isMobile) {
					// Show the button only when the chartWrapper is hovered
					chartWrapper.addEventListener('mouseenter', () => {
						aiButton!.style.opacity = '1';
					});
					chartWrapper.addEventListener('mouseleave', () => {
						aiButton!.style.opacity = '0';
					});
				}
			}

			if (aiButton) topRightContainer.appendChild(aiButton);
			const menuButton = createChartMenuButton(chart, state, chartWrapper);
			if (menuButton) {
				topRightContainer.appendChild(menuButton);
			}
			chartWrapper.appendChild(topRightContainer);

			let fullscreenIconDiv: HTMLDivElement | undefined;
			if (chart.name && !isStatic) {
				const cnContainer = document.createElement('div');
				cnContainer.className = 'chart-title';
				Object.assign(cnContainer.style, {
					display: 'flex',
					alignItems: 'center',
					gap: '8px',
					fontWeight: '600',
					fontSize: '14px',
					lineHeight: '20px',
					paddingRight: '56px',
					marginBottom: '4px',
				});

				const cn = document.createElement('span');
				cn.textContent = chart.name;
				cnContainer.appendChild(cn);
				if (state.onOpenFullscreen && !isSingleValueChart(chart.representation)) {
					fullscreenIconDiv = document.createElement('div');
					Object.assign(fullscreenIconDiv.style, {
						cursor: 'pointer',
						flexShrink: '0',
						opacity: '0.7',
						transition: 'opacity 0.2s ease',
						display: 'flex',
						alignItems: 'center',
						position: 'relative'
					});

					const beforeElement = createLinkTooltip(translateToString('dashboard.maximize'));

					fullscreenIconDiv.appendChild(beforeElement);
					fullscreenIconDiv.setAttribute('role', 'button');
					fullscreenIconDiv.tabIndex = 0;

					const fullscreenIcon = createFullscreenIcon();
					fullscreenIconDiv.appendChild(fullscreenIcon);
					cnContainer.appendChild(fullscreenIconDiv);
				}
				// Add external link icon if onSeeReport is available
				const reportId = chart.report_id;
				if (state.onSeeReport && reportId != null) {
					const linkIconDiv = document.createElement('div');
					Object.assign(linkIconDiv.style, {
						cursor: 'pointer',
						flexShrink: '0',
						opacity: '0.7',
						transition: 'opacity 0.2s ease',
						display: 'flex',
						alignItems: 'center',
						position: 'relative'
					});

					const beforeElement = createLinkTooltip(translateToString('dashboard.linked_report'));

					linkIconDiv.appendChild(beforeElement);

					linkIconDiv.setAttribute('role', 'button');
					linkIconDiv.tabIndex = 0;

					const linkIcon = createExternalLinkIcon();
					linkIconDiv.appendChild(linkIcon);
					linkIconDiv.onclick = () => state.onSeeReport?.(reportId);
					cnContainer.appendChild(linkIconDiv);
				}

				chartWrapper.appendChild(cnContainer);
			}
			const svgContainer = document.createElement('div');
			svgContainer.className = 'chart-svg-container';
			Object.assign(svgContainer.style, {
				width: '100%',
				height: '100%',
				minHeight: isSingleValueChart(chart.representation) || isStatic ? '0' : '120px',
				display: 'flex',
				justifyContent: chart.representation === 'bar' ? 'flex-start' : 'center',
				alignItems: chart.representation === 'bar' ? 'flex-start' : 'center',
				overflow: chart.representation === 'bar' ? 'auto' : 'visible',
			});
			chartWrapper.appendChild(svgContainer);

			if (isStatic) {
				svgContainer.appendChild(createStaticWidget(chart));
				return;
			}

			const loader = createCenteredLoader();
			svgContainer.appendChild(loader);

			const renderChart = async(measuredWidth?: number) => {
				const measured = measuredWidth ?? svgContainer.getBoundingClientRect().width;
				const width = measured && measured > 0 ? measured : 600; // fallback width
				try {
					const { report, definition } = await getReport({
						companyId: state.companyId,
						userId: state.userId,
						filterDates: state.filterDates,
						products: state.rawProducts,
						foundationUrl: state.foundationUrl,
						bearerToken: state.authToken,
						id: chart.report_id ?? undefined
					});
					const goalTree = await getGoalsAndGenerateTree(chart, definition, report, state);
					const props: GenerateChartSvgProps = {
						report, chart, state, width, goalTree, setData: (data) => {
							state.onChartDataLoad?.(chart.uuid, data);
							if (aiButton)
								aiButton.onclick = (() => {
									state.onAiChat?.(chart, data);
									return;

								});
						}, height: undefined, onNavigateInData: (svg, breadCrumb) => {
							svgContainer.innerHTML = '';
							svgContainer.style.removeProperty('display');
							svgContainer.style.removeProperty('justify-content');
							svgContainer.style.removeProperty('align-items');
							if (svg) {
								if (breadCrumb) svgContainer.appendChild(breadCrumb);
								svgContainer.appendChild(svg);
							} else {
								svgContainer.textContent = translateToString('dashboard.errors.no_chart');
							}
						}, filterPath: []
					};
					if (fullscreenIconDiv) fullscreenIconDiv.onclick = () => state.onOpenFullscreen?.(props);
					const result = generateChartSvg(props);
					// Remove loader and add chart
					svgContainer.removeChild(loader);
					if (result) {
						svgContainer.appendChild(result.element);
						// Store cleanup function on the element for later cleanup
						if (result.cleanup) {
							(result.element as ElementWithCleanup)._cleanupTooltip = result.cleanup;
						}
					} else {
						svgContainer.textContent = translateToString('dashboard.errors.no_chart');
					}
				} catch (_e) {
					// Remove loader and show error
					console.log(_e);
					svgContainer.removeChild(loader);
					svgContainer.textContent = translateToString('dashboard.errors.failed_to_load_chart');
				}
			};

			let observer: ResizeObserver | null = null;
			try {
				observer = new ResizeObserver(entries => {
					for (const entry of entries) {
						const w = entry.contentRect?.width ?? svgContainer.getBoundingClientRect().width;
						if (w && w > 0) {
							if (observer) observer.disconnect();
							renderChart(w);
							break;
						}
					}
				});
				observer.observe(svgContainer);
			} catch (_) {
				setTimeout(() => renderChart(), 50);
			}
		});
	}
}

export type ValueNameData = { value: number; name: string, nodeValue: InnerValue };

export type GroupedValueNameData = { value: number; name: string; group: string, nodeValue: InnerValue };

export const sidelyChartColors = [
	'#737ef0',
	CeruleanSidely,
	ForestGreenSidely,
	TealSidely,
	BrightTealSidely,
	SteelBlueSidely,
	EmeraldSidely,
	GreenSidely,
	PeriwinkleBlueSidely,
	SlateSidely,
	IndigoSidely,
	PlumSidely,
	SkyBlueSidely,
	MauveSidely,
	MediumPurpleSidely,
	LimeGreenSidely,
	CoolGreySidely,
	BrownSidely,
	LavenderSidely,
	LightTealSidely,
	BlueGreySidely,
	OliveSidely,
	SiennaSidely,
	YellowGreenSidely,
	LightCyanSidely,
	LightLavenderSidely,
	BurntOrangeSidely,
	PeachSidely,
	LimeYellowSidely,
	YellowSidely,
	LightPeachSidely,
	LightPinkSidely,
	BrightRedSidely,
	SalmonSidely,
	RedSidely,
	BlushSidely,
	OrangeSidely,
	AmberSidely,
	CoralSidely,
	BrightYellowSidely
];

export function getGroupColor(name: string): string {
	if (!name) return sidelyChartColors[0];
	if (name && name.length > 0) {
		let total = 0;
		for (let i = 0; i < name.length; i++) {
			total += name.charCodeAt(i);
		}
		const index = total % sidelyChartColors.length;
		return sidelyChartColors[index];
	} else return sidelyChartColors[0];
}

export function roundDigitsAfterDecimal(value: number, digitsAfterDecimal?: number): number {
	if (!isFinite(value) || digitsAfterDecimal == null || digitsAfterDecimal < 0) return value;
	const digits = Math.floor(digitsAfterDecimal);
	const factor = Math.pow(10, digits);
	return Math.round(value * factor) / factor;
}

export type InnerValue = Value['val'];

class ReportChartData {
	value: InnerValue | InnerValue[];
	path: InnerValue[];
	leaf: InnerValue;

	constructor({ value, path, leaf }: { value: InnerValue | InnerValue[], path: InnerValue[], leaf: InnerValue }) {
		this.value = value;
		this.path = path;
		this.leaf = leaf;
	}

	private _getSingleValue(): InnerValue {
		if (Array.isArray(this.value)) {
			return this.value[0];
		}
		return this.value;
	}

	toValueNameData(state: DashboardState): ValueNameData {
		const title = valueInnerToString(this.leaf, state) ?? '';
		return ({
			nodeValue: this.leaf,
			value: innerValueToNumber(this._getSingleValue()) ?? 0,
			name: title,
		});
	}

	toDateValueData(headers: Value[], state: DashboardState): GroupedValueNameData[] {
		const title = valueInnerToString(this.leaf, state) ?? '';
		if (!Array.isArray(this.value)) return [];
		const dateValueData: GroupedValueNameData[] = this.value.reduce((acc: GroupedValueNameData[], val, index) => {
			const valueNumber = innerValueToNumber(val);
			if (valueNumber != undefined) {
				acc.push({
					nodeValue: this.leaf,
					value: valueNumber,
					name: valueToString(headers[index], state) ?? '',
					group: title,
				});
			}
			return acc;
		}, []);
		return dateValueData;
	}

	toGroupedValueNameData(headers: Value[], state: DashboardState): GroupedValueNameData[] {
		const title = valueInnerToString(this.leaf, state) ?? '';
		if (!Array.isArray(this.value)) return [];
		const groupBarChartData: GroupedValueNameData[] = this.value.map((val, index) => {
			return ({
				nodeValue: this.leaf,
				value: innerValueToNumber(val) ?? 0,
				name: valueToString(headers[index], state) ?? '',
				group: title,
			});
		});
		return groupBarChartData;
	}
}

export function formatValue(v: number, opts?: { locale?: string, number_extension?: string | number }): string {
	const formatted = v.toLocaleString(opts?.locale ?? 'fr-FR');
	const extRaw = opts?.number_extension;
	if (!extRaw) return formatted;
	const ext = String(extRaw).trim();
	// If extension starts with an ASCII letter, put a space before it,
	// otherwise (symbols like % or €) join without space. Use ASCII-only regex
	// to avoid requiring a Unicode regex flag (keeps compatibility with older TS targets).
	const needsSpace = /^[A-Za-z]/.test(ext.charAt(0));
	return needsSpace ? `${formatted} ${ext}` : `${formatted}${ext}`;
}

export function trimLabel(text: string, maxLength?: number): string {
	if (maxLength === undefined || maxLength <= 0 || text.length <= maxLength) {
		return text;
	}
	const trimLength = Math.max(1, maxLength);
	return text.slice(0, trimLength) + '...';
}

/**
 * Unified date formatting function that uses Intl.DateTimeFormat for locale-aware formatting
 */
export function displayDateFormat(date: Date, granularity: 'day' | 'week' | 'month' | 'year' = 'month', locale: string = 'fr-FR'): string {
	switch (granularity) {
		case 'year':
			return new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(date);
		case 'month': {
			const monthFormat = new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(date);
			return monthFormat.replace(/\.$/, '');
		}
		case 'week': {
			const weekFormat = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
			return weekFormat.replace(/\.$/, '');
		}
		case 'day':
		default: {
			const dayFormat = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
			return dayFormat.replace(/\.$/, '');
		}
	}
}

export function createLegend<T extends d3.BaseType>(
	svg: d3.Selection<T, unknown, null, undefined>,
	options: {
		sortedKeys: string[],
		width: number,
		marginLeft: number,
		marginRight: number,
		leftPosition: number,
		marginTop: number,
		itemSpacing?: number,
		maxItems?: number,
		maxChars?: number,
		legendOrientation?: 'horizontal' | 'vertical',
		values?: Record<string, string>,
		details?: Record<string, string>,
		itemHeight?: number,
		verticalWidth?: number,
		position?: { x: number, y: number, centerY?: boolean },
	}
): d3.Selection<SVGGElement, unknown, null, undefined> | null {
	const { sortedKeys, width, marginLeft, marginRight, marginTop, leftPosition, itemSpacing = 100, maxChars, legendOrientation = 'horizontal', values, details, itemHeight = 16, verticalWidth, position } = options;

	// Don't show legend if any series has an empty key
	const hasEmptyKey = sortedKeys.some(k => !k);
	if (hasEmptyKey) return null;

	const horizontal = legendOrientation === 'horizontal';

	// Calculate how many items can fit if maxItems not provided
	const defaultMaxItems = horizontal
		? Math.floor((width - marginLeft - marginRight) / itemSpacing)
		: Math.floor((marginTop - 40) / itemHeight);

	const effectiveMaxItems = options.maxItems ?? Math.max(1, defaultMaxItems);

	// Don't show pagination if we have fewer items than effectiveMaxItems
	const needsPagination = sortedKeys.length > effectiveMaxItems;

	// State for pagination
	let currentOffset = 0;
	const itemsPerPage = effectiveMaxItems;
	const totalPages = Math.ceil(sortedKeys.length / itemsPerPage);

	// Calculate dimensions
	const arrowSize = needsPagination ? 20 : 0;
	const arrowSpacing = needsPagination ? 8 : 0;
	const availableWidth = horizontal ? (itemsPerPage * itemSpacing) : (verticalWidth ?? 160);
	const availableHeight = Math.max(1, itemsPerPage) * itemHeight;

	// For horizontal: arrows on left/right, for vertical: arrows on top/bottom
	const legendWidth = horizontal
		? availableWidth + (needsPagination ? (arrowSize * 2 + arrowSpacing * 2) : 0)
		: availableWidth;
	const legendHeight = horizontal
		? itemHeight
		: availableHeight + (needsPagination ? (arrowSize * 2 + arrowSpacing * 2) : 0);

	// For horizontal: left-align, for vertical: right-align
	const legendX = position ? position.x : horizontal
		? leftPosition
		: Math.max(marginLeft, width - marginRight - legendWidth);
	const legendY = position ? (position.centerY ? position.y - legendHeight / 2 : position.y) : Math.max(8, marginTop - legendHeight - 8);

	const legend = svg.append('g').attr('class', 'd3-legend').attr('transform', `translate(${legendX},${legendY})`);

	// Rough character-fit calculation to decide truncation length
	const avgCharWidth = 7; // px estimate for monospace-ish average
	const calculatedMaxChars = maxChars ?? (horizontal
		? Math.max(6, Math.floor((itemSpacing - 28) / avgCharWidth))
		: Math.max(6, Math.floor((availableWidth - 28) / avgCharWidth)));

	// Function to update legend items display
	const updateLegendItems = () => {
		// Remove existing items
		legend.selectAll('.legend-item').remove();

		// Get current page items
		const startIndex = currentOffset * itemsPerPage;
		const endIndex = Math.min(startIndex + itemsPerPage, sortedKeys.length);
		const currentItems = sortedKeys.slice(startIndex, endIndex);

		// Add current page items
		currentItems.forEach((k, i) => {
			const xOff = horizontal
				? (i * itemSpacing + (needsPagination ? arrowSize + arrowSpacing : 0))
				: 0;
			const yOff = horizontal
				? 0
				: (i * itemHeight + (needsPagination ? arrowSize + arrowSpacing : 0));
			const g = legend.append('g').attr('class', 'legend-item').attr('transform', `translate(${xOff}, ${yOff})`);
			g.append('circle').attr('cx', 5).attr('cy', itemHeight / 2 - 2).attr('r', 4).attr('fill', getGroupColor(k));
			const value = values?.[k];
			const detail = details?.[k];
			const valueText = value !== undefined ? `${value}${detail ? ` (${detail})` : ''}` : '';
			const itemMaxChars = value !== undefined && !horizontal
				? Math.max(4, Math.floor((availableWidth - 28 - valueText.length * avgCharWidth) / avgCharWidth))
				: calculatedMaxChars;
			const label = (k && k.length > itemMaxChars) ? (k.slice(0, Math.max(0, itemMaxChars - 1)) + '…') : k;
			const text = g.append('text').attr('x', 16).attr('y', itemHeight / 2 + 2).attr('font-size', 11).attr('fill', SidelyBlack).text(label);
			text.append('title').text(k);
			if (value !== undefined) {
				const valueEl = g.append('text')
					.attr('x', horizontal ? 16 + label.length * avgCharWidth + 6 : availableWidth)
					.attr('y', itemHeight / 2 + 2)
					.attr('text-anchor', horizontal ? 'start' : 'end')
					.attr('font-size', 11)
					.attr('fill', SidelyBlack);
				valueEl.append('tspan').text(value);
				if (detail) valueEl.append('tspan').attr('fill', DarkGreySidely2).text(` (${detail})`);
			}
		});
	};

	// Add navigation arrows if needed
	if (needsPagination) {
		if (horizontal) {
			// Left arrow for horizontal layout
			const leftArrow = legend.append('g').attr('class', 'legend-arrow-left').style('cursor', 'pointer');
			leftArrow.append('rect')
				.attr('width', arrowSize)
				.attr('height', legendHeight)
				.attr('fill', 'transparent')
				.style('cursor', 'pointer');
			leftArrow.append('polygon')
				.attr('points', `${arrowSize - 6},${legendHeight / 2 - 4} ${arrowSize - 6},${legendHeight / 2 + 4} ${arrowSize - 12},${legendHeight / 2}`)
				.attr('fill', DarkGreySidely2)
				.attr('stroke', 'none');

			// Right arrow for horizontal layout
			const rightArrowX = availableWidth + arrowSize + arrowSpacing;
			const rightArrow = legend.append('g').attr('class', 'legend-arrow-right').style('cursor', 'pointer')
				.attr('transform', `translate(${rightArrowX}, 0)`);
			rightArrow.append('rect')
				.attr('width', arrowSize)
				.attr('height', legendHeight)
				.attr('fill', 'transparent')
				.style('cursor', 'pointer');
			rightArrow.append('polygon')
				.attr('points', `6,${legendHeight / 2 - 4} 6,${legendHeight / 2 + 4} 12,${legendHeight / 2}`)
				.attr('fill', DarkGreySidely2)
				.attr('stroke', 'none');

			// Arrow click handlers
			leftArrow.on('click', (event) => {
				event.stopPropagation();
				currentOffset = currentOffset > 0 ? currentOffset - 1 : totalPages - 1;
				updateLegendItems();
			});

			rightArrow.on('click', (event) => {
				event.stopPropagation();
				currentOffset = (currentOffset + 1) % totalPages;
				updateLegendItems();
			});
		} else {
			// Up arrow for vertical layout
			const upArrow = legend.append('g').attr('class', 'legend-arrow-up').style('cursor', 'pointer');
			upArrow.append('rect')
				.attr('width', availableWidth)
				.attr('height', arrowSize)
				.attr('fill', 'transparent')
				.style('cursor', 'pointer');
			upArrow.append('polygon')
				.attr('points', `${availableWidth / 2 - 4},${arrowSize - 6} ${availableWidth / 2 + 4},${arrowSize - 6} ${availableWidth / 2},${arrowSize - 12}`)
				.attr('fill', DarkGreySidely2)
				.attr('stroke', 'none');

			// Down arrow for vertical layout
			const downArrowY = availableHeight + arrowSize + arrowSpacing;
			const downArrow = legend.append('g').attr('class', 'legend-arrow-down').style('cursor', 'pointer')
				.attr('transform', `translate(0, ${downArrowY})`);
			downArrow.append('rect')
				.attr('width', availableWidth)
				.attr('height', arrowSize)
				.attr('fill', 'transparent')
				.style('cursor', 'pointer');
			downArrow.append('polygon')
				.attr('points', `${availableWidth / 2 - 4},6 ${availableWidth / 2 + 4},6 ${availableWidth / 2},12`)
				.attr('fill', DarkGreySidely2)
				.attr('stroke', 'none');

			// Arrow click handlers
			upArrow.on('click', (event) => {
				event.stopPropagation();
				currentOffset = currentOffset > 0 ? currentOffset - 1 : totalPages - 1;
				updateLegendItems();
			});

			downArrow.on('click', (event) => {
				event.stopPropagation();
				currentOffset = (currentOffset + 1) % totalPages;
				updateLegendItems();
			});
		}
	}

	// Initial render
	updateLegendItems();

	return legend;
}

export function shouldShowLegend(opts?: { legend?: boolean }, sortedKeys?: string[]): boolean {
	if (!opts?.legend || !sortedKeys) return false;
	// Don't show legend if any series has an empty key
	const hasEmptyKey = sortedKeys.some(k => !k);
	return !hasEmptyKey;
};

// ============================================================================
// TOOLTIP UTILITIES
// ============================================================================

type TooltipConfig = {
	tooltipId?: string;
	className?: string;
	styles?: Partial<CSSStyleDeclaration>;
};

type TooltipPositionOptions = {
	preferSide?: 'left' | 'right';
	margin?: number;
	offsetX?: number;
	offsetY?: number;
};

/**
 * Creates or gets a reusable HTML tooltip element
 */
export function createTooltip(config?: TooltipConfig): HTMLDivElement {
	const tooltipId = config?.tooltipId ?? 'global';
	const className = config?.className ?? `d3-tooltip-${tooltipId}`;

	// Remove any existing tooltip to avoid DOM leaks
	const existing = document.querySelector(`.${className}`) as HTMLDivElement;
	if (existing) {
		existing.remove();
	}

	const tooltip = document.createElement('div');
	tooltip.className = className;

	// Default tooltip styles
	const defaultStyles: Partial<CSSStyleDeclaration> = {
		position: 'absolute',
		pointerEvents: 'none',
		background: `color-mix(in oklab, ${SidelyBlack} 75%, transparent)`,
		color: 'white',
		padding: '6px 8px',
		borderRadius: '4px',
		fontSize: '12px',
		zIndex: '300',
		display: 'none',
		fontFamily: '\'Inter\', sans-serif'
	};

	// Apply styles
	Object.assign(tooltip.style, defaultStyles, config?.styles);

	document.body.appendChild(tooltip);
	return tooltip;
}

/**
 * Positions tooltip with bounds checking and collision avoidance
 */
export function positionTooltip(
	tooltip: HTMLDivElement,
	event: MouseEvent,
	options?: TooltipPositionOptions
): void {
	const opts = {
		margin: 8,
		offsetX: 12,
		offsetY: -12,
		...options
	};

	const e = event as MouseEvent & { pageX: number; pageY: number };
	let x = e.pageX + opts.offsetX;
	let y = e.pageY + opts.offsetY;

	// Show tooltip temporarily to measure its size
	tooltip.style.left = `${x}px`;
	tooltip.style.top = `${y}px`;
	tooltip.style.display = 'block';

	const rect = tooltip.getBoundingClientRect();
	const viewportWidth = window.innerWidth;
	const viewportHeight = window.innerHeight;

	// Handle preference for specific side
	if (opts.preferSide === 'left') {
		x = e.pageX - rect.width - Math.abs(opts.offsetX);
	} else if (opts.preferSide === 'right') {
		x = e.pageX + Math.abs(opts.offsetX);
	}

	// Horizontal overflow protection
	if (x + rect.width > viewportWidth - opts.margin) {
		x = e.pageX - rect.width - Math.abs(opts.offsetX);
	}
	if (x < opts.margin) {
		x = opts.margin;
	}

	// Vertical overflow protection
	if (y < opts.margin) {
		y = e.pageY + Math.abs(opts.offsetY);
	}
	if (y + rect.height > viewportHeight - opts.margin) {
		y = e.pageY - rect.height - Math.abs(opts.offsetY);
	}
	if (y < opts.margin) {
		y = opts.margin;
	}

	tooltip.style.left = `${x}px`;
	tooltip.style.top = `${y}px`;
}

/**
 * Shows tooltip with content at mouse position
 */
export function showTooltip(
	tooltip: HTMLDivElement,
	content: string,
	event: MouseEvent,
	options?: TooltipPositionOptions
): void {
	tooltip.innerHTML = content;
	positionTooltip(tooltip, event, options);
}

/**
 * Hides tooltip
 */
export function hideTooltip(tooltip: HTMLDivElement): void {
	tooltip.style.display = 'none';
}

/**
 * Sets up global tooltip hiding on scroll, wheel, and touch events
 */
export function setupTooltipHiding(
	tooltip: HTMLDivElement,
	additionalCleanup?: () => void
): () => void {
	const hideAll = () => {
		try {
			hideTooltip(tooltip);
			additionalCleanup?.();
		} catch (_e) {
			// Ignore errors during cleanup
		}
	};

	window.addEventListener('scroll', hideAll, { passive: true });
	window.addEventListener('wheel', hideAll, { passive: true });
	window.addEventListener('touchstart', hideAll, { passive: true });

	// Return cleanup function
	return () => {
		window.removeEventListener('scroll', hideAll);
		window.removeEventListener('wheel', hideAll);
		window.removeEventListener('touchstart', hideAll);
	};
}

/**
 * Positions tooltip at a specific screen coordinate (for focus markers)
 */
export function positionTooltipAt(
	tooltip: HTMLDivElement,
	screenX: number,
	screenY: number,
	options?: TooltipPositionOptions & { focusScreenX?: number }
): void {
	const opts = {
		margin: 8,
		offsetX: 12,
		offsetY: -12,
		...options
	};

	let x = screenX + opts.offsetX;
	let y = screenY + opts.offsetY;

	// Show tooltip temporarily to measure its size
	tooltip.style.left = `${x}px`;
	tooltip.style.top = `${y}px`;
	tooltip.style.display = 'block';

	const rect = tooltip.getBoundingClientRect();
	const viewportWidth = window.innerWidth;

	// Prefer side opposite the focus marker when provided
	if (typeof opts.focusScreenX === 'number') {
		if (opts.focusScreenX < viewportWidth / 2) {
			// Focus is on left side, prefer tooltip on right
			x = screenX + Math.abs(opts.offsetX);
		} else {
			// Focus is on right side, prefer tooltip on left
			x = screenX - rect.width - Math.abs(opts.offsetX);
		}
	}

	// Bounds checking
	if (x + rect.width > viewportWidth - opts.margin) {
		x = screenX - rect.width - Math.abs(opts.offsetX);
	}
	if (x < opts.margin) {
		x = opts.margin;
	}

	if (y < opts.margin) {
		y = screenY + Math.abs(opts.offsetY);
	}
	if (y < opts.margin) {
		y = opts.margin;
	}

	tooltip.style.left = `${x}px`;
	tooltip.style.top = `${y}px`;
}

/**
 * Creates a specialized tooltip for Number charts with click/touch support
 */
export function createNumberTooltip(
	container: HTMLElement,
	content: () => string,
	config?: TooltipConfig
): { tooltip: HTMLDivElement; cleanup: () => void } {
	const tooltip = createTooltip(config);
	let tooltipVisible = false;

	const populateTooltip = () => {
		tooltip.innerHTML = content();
	};

	const moveTooltip = (e: MouseEvent) => {
		positionTooltip(tooltip, e);
	};

	const showTooltipAt = (pageX: number, pageY: number) => {
		populateTooltip();
		const fakeEvent = { pageX, pageY } as unknown as MouseEvent;
		positionTooltip(tooltip, fakeEvent);
		tooltipVisible = true;
	};

	const hideTooltipHandler = () => {
		tooltipVisible = false;
		hideTooltip(tooltip);
	};

	// Mouse event handlers
	const mouseEnterHandler = () => {
		populateTooltip();
		tooltipVisible = true;
		tooltip.style.display = 'block';
	};

	const mouseleaveHandler = hideTooltipHandler;

	const clickHandler = (e: MouseEvent) => {
		const ev = e as MouseEvent & { pageX: number; pageY: number };
		if (!tooltipVisible) {
			showTooltipAt(ev.pageX, ev.pageY);
		} else {
			hideTooltipHandler();
		}
		e.stopPropagation();
	};

	const touchStartHandler = (e: TouchEvent) => {
		const t = e.touches && e.touches[0];
		if (t) {
			if (!tooltipVisible) {
				showTooltipAt(t.pageX, t.pageY);
			} else {
				hideTooltipHandler();
			}
		}
		e.preventDefault();
		e.stopPropagation();
	};

	// Global click/touch handlers to hide tooltip when clicking outside
	const globalHide = (ev: Event) => {
		if (!container.contains(ev.target as Node) && tooltipVisible) {
			hideTooltipHandler();
		}
	};

	// Add event listeners
	container.addEventListener('mouseenter', mouseEnterHandler);
	container.addEventListener('mousemove', moveTooltip);
	container.addEventListener('mouseleave', mouseleaveHandler);
	container.addEventListener('click', clickHandler);
	container.addEventListener('touchstart', touchStartHandler);
	document.addEventListener('click', globalHide);
	document.addEventListener('touchstart', globalHide, { passive: true });

	// Return cleanup function
	const cleanup = () => {
		container.removeEventListener('mouseenter', mouseEnterHandler);
		container.removeEventListener('mousemove', moveTooltip);
		container.removeEventListener('mouseleave', mouseleaveHandler);
		container.removeEventListener('click', clickHandler);
		container.removeEventListener('touchstart', touchStartHandler);
		document.removeEventListener('click', globalHide);
		document.removeEventListener('touchstart', globalHide);
		hideTooltip(tooltip);
	};

	return { tooltip, cleanup };
}

type Fraction = {
	readonly numer: number,
	readonly denom: number,
} | [number, number] | number;

function fractionToDecimal(f: Fraction): number {
	if (Array.isArray(f)) return f[0] / f[1];
	if (typeof f === 'number') return f;
	return f.numer / f.denom;
}

function fractionToPercentage(f: Fraction): number {
	return fractionToDecimal(f) * 100;
}

export function innerValueToNumber(value: Value['val']): number | null {
	if (typeof value === 'string' || !value) return null;
	if ('int' in value) return value.int;
	if ('float' in value) return value.float;
	if ('percentage' in value) return fractionToPercentage(value.percentage);
	if ('average' in value) return value.average.numerator / value.average.denominator;
	return null;
}

function valueToNumber(value: Value): number | null {
	if (typeof value === 'string' || !value || !value.val) return null;
	return innerValueToNumber(value.val);
}

function valueInnerToString(value: ReportColumnType, state: DashboardState): string | undefined {
	if (typeof value === 'string' || !value) {
		return '';
	} else if ('text' in value) {
		return value.text;
	} else if ('key' in value) {
		return translateToString(value.key);
	} else if ('formattedKey' in value) {
		let res = translateToString(value.formattedKey.key);
		value.formattedKey.formats.forEach((replacementKey, i) => res = res?.replaceAll(`{{${i + 1}}}`, replacementKey));
		return res;
	} else if ('int' in value) {
		return value.int.toString();
	} else if ('float' in value) {
		return value.float.toFixed(2);
	} else if ('date' in value) {
		return Object.values(value.date.val)[0];
	} else if ('evolution' in value) {
		return value.evolution.toString();
	} else if ('company' in value) {
		const companies: DashboardState['companies'] = state.companies;
		return companies?.get(value.company) ?? `Company #${value.company}`;
	} else if ('list' in value) {
		return value.list.map(e => e.value).join(', ');
	} else if ('user' in value) {
		const users: DashboardState['users'] = state.users;
		return users?.get(value.user) ?? `User #${value.user}`;
	} else if ('datetime' in value) {
		const date = new Date(value.datetime * 1000);
		return date.toLocaleDateString() + ' - ' + date.toLocaleTimeString();
	} else if ('product' in value) {
		const products: DashboardState['products'] = state.products;
		return products?.get(value.product) ?? `Product ${value.product}`;
	} else if ('colorDot' in value) {
		return value.colorDot;
	} else if ('average' in value) {
		return (value.average.numerator / value.average.denominator).toFixed(2);
	} else if ('event' in value) {
		return value.event.title;
	} else if ('contact' in value) {
		return `Contact #${value.contact}`;
	} else if ('campaign' in value) {
		const campaigns: DashboardState['campaigns'] = state.campaigns;
		return campaigns?.get(value.campaign) ?? `Campaign #${value.campaign}`;
	} else {
		console.warn('unimplemented value :', value);
	}
}

export function valueToString(value: Value | undefined, state: DashboardState): string | undefined {
	if (typeof value === 'string' || !value || !value.val) {
		return '';
	}
	return valueInnerToString(value.val, state);
}

function getChartHeadersFromReport(report: BareReport, chartData: ChartData): Value[] {
	if (!report.panels[chartData.panel_index]) return [];
	if (chartData.values_index === undefined) return report.panels[chartData.panel_index].headers.filter(h => !h.primary).map(h => h.cell.value);
	const header = report.panels[chartData.panel_index].headers.filter(h => !h.primary)[chartData.values_index];
	return header ? [header.cell.value] : [];
}

function getChartFootersFromReport(report: BareReport, chartData: ChartData, nonPrimaryFooterStartIndex): Value[] {
	if (!report.panels[chartData.panel_index]) return [];
	if (chartData.values_index === undefined) return report.panels[chartData.panel_index].footers.slice(nonPrimaryFooterStartIndex).map(h => h.value);
	return [report.panels[chartData.panel_index].footers.slice(nonPrimaryFooterStartIndex)[chartData.values_index]?.value];
}

function getChartDataFromReport(report: BareReport, chartData: ChartData, filterPath: InnerValue[]): ReportChartData[] {
	if (!report.panels[chartData.panel_index]) return [];
	const res = report.panels[chartData.panel_index].rows.reduce<ReportChartData[]>((acc: ReportChartData[], row) => {
		acc.push(...getChartDataFromRow(row, chartData.group_by_index, chartData.values_index, filterPath));
		return acc;
	}, []);
	res.forEach(node => node.path.reverse());
	return res;
}

function getChartDataFromGoalTree(tree: GoalTree, chartData: ChartData, filterPath: InnerValue[]): ReportChartData[] {
	const res = getChartDataFromGoalTreeRec(tree, chartData.group_by_index, chartData.values_index, filterPath);
	res.forEach(node => node.path.reverse());
	return res;
}

function getChartDataFromGoalTreeRec(
	tree: GoalTree,
	groupByIndex: number,
	valueIndex: number | undefined,
	filterPath: InnerValue[],
	depth: number = 0
): ReportChartData[] {
	if (depth > (groupByIndex + filterPath.length)) {
		return [];
	} else if (depth == (groupByIndex + filterPath.length)) {
		const res: ReportChartData[] = [];
		for (const node of tree.values()) {
			res.push(new ReportChartData({
				path: [],
				leaf: node.primaryValue,
				// Todo make it wotks with valueIndex === undefined
				value: node.evolution ?? null
			}));
		}
		return res;
	} else {
		const res: ReportChartData[] = [];
		const pathHash = depth < groupByIndex ? '' : goalPathHash(filterPath[depth - groupByIndex]);
		for (const node of tree.values()) {
			if (filterPath.length === 0 || depth < groupByIndex || pathHash === goalPathHash(node.primaryValue))
				if (node.children) {
					const nodeRes = getChartDataFromGoalTreeRec(node.children, groupByIndex, valueIndex, filterPath, depth + 1);
					nodeRes.forEach(subNode => {
						subNode.path.push(node.primaryValue);
					});
					res.push(
						...nodeRes
					);
				}
		}
		return res;
	}
}

function getChartDataFromRow(
	row: Row,
	groupByIndex: number,
	valueIndex: number | undefined,
	filterPath: InnerValue[],
	depth: number = 0
): ReportChartData[] {
	if (depth > (groupByIndex + filterPath.length)) {
		return [];
	} else if (depth == (groupByIndex + filterPath.length)) {
		return [
			new ReportChartData({
				path: [],
				leaf: row.primaryCell[groupByIndex + filterPath.length].value.val,
				value: valueIndex === undefined ? row.cells.map(cell => cell.value.val) : row.cells[valueIndex].value.val
			})
		];
	} else {
		const primaryCell = row.primaryCell[depth];
		if (!primaryCell) return [];
		const res: ReportChartData[] = [];
		const pathHash = depth < groupByIndex ? '' : goalPathHash(filterPath[depth - groupByIndex]);
		row.rows.forEach(row => {
			if (filterPath.length === 0 || depth < groupByIndex || (pathHash === goalPathHash(primaryCell.value.val)))
				res.push(
					...getChartDataFromRow(row, groupByIndex, valueIndex, filterPath, depth + 1)
				);
		});
		res.forEach(test => {
			test.path.push(primaryCell.value.val);
		});
		return res;
	}
}

function prepareChartDataFromRaw(raw: ReportChartData[], headers: Value[], chart: DashboardChartConfigWithChartData, state: DashboardState, filterPath: InnerValue[], report: BareReport, footersLength: number): (ValueNameData | GroupedValueNameData[])[] {
	const result: (ValueNameData | GroupedValueNameData[])[] = [];
	if (chart.data.footer_values_only) {
		const footers = getChartFootersFromReport(report, chart.data, footersLength);
		return footers ? footers.map((footer, index) => {
			return {
				nodeValue: headers[index]['val'],
				value: valueToNumber(footer) ?? 0,
				name: valueToString(headers[index], state) ?? '',
			};
		}) : [];
	}
	raw.forEach(data => {
		if (!data.value) return;
		let skipDataPoint = false;
		if (chart.data.filters_path && data.path && Array.isArray(data.path)) {
			for (const [filterIndexStr, filterPathItem] of Object.entries(chart.data.filters_path)) {
				const filterIndex = parseInt(filterIndexStr);
				const pathValue = valueInnerToString(filterIndex === chart.data.group_by_index + filterPath.length ? data.leaf : data.path[filterIndex], state);
				if (!pathValue || (pathValue && !filterPathItem.path.includes(pathValue))) {
					skipDataPoint = true;
					break;
				}
			}
		}
		if (skipDataPoint) return;
		switch (chart.representation) {
			case 'pie': {
				const d = data.toValueNameData(state);
				if (d && (chart.data.goals || d.value)) result.push(d);
				break;
			}
			case 'bar': {
				if (Array.isArray(data.value)) {
					const groupedChartData = data.toGroupedValueNameData(headers, state);
					groupedChartData.forEach(d => { if (d.value) result.push(d); });
				} else {
					const d = data.toValueNameData(state);
					if (d && (chart.data.goals || d.value)) result.push(d);
				}
				break;
			}
			case 'line': {
				const dateValueData = data.toDateValueData(headers, state);
				dateValueData.forEach(d => result.push(d));
				break;
			}
			case 'number':
			case 'gauge': {
				if (Array.isArray(data.value)) {
					const d = data.toValueNameData(state); if (d && d.value) result.push(d);
				} else {
					const numberData = data.toValueNameData(state);
					result.push(numberData);
				}
				break;
			}
			default:
				break;
		}
	});
	return result;
}

export async function getDashboards(foundationUrl: string, bearerToken: string, type?: string): Promise<Dashboard[]> {
	return await fetch(`${foundationUrl}/api/v2/dashboard${type ? `?type=${type}` : ''}`, {
		method: 'GET',
		headers: {
			'Authorization': `Bearer ${bearerToken}`,
			'Content-Type': 'application/json'
		}
	})
		.then(response => response.json());
}

export async function getReport({
	companyId,
	userId,
	filterDates,
	products,
	foundationUrl,
	bearerToken,
	id,
	report
}
	: {
		companyId: number | undefined,
		userId: number | undefined,
		filterDates: OnDateChangeType | undefined,
		products: AtomProducts[],
		foundationUrl?: string,
		bearerToken?: string,
		id?: number,
		report?: Report
	}): Promise<{ report: BareReport, definition: Report }> {
	const genericReport: Report = report ?? await fetch(`${foundationUrl}/api/v2/reports/generic/${id}`, {
		method: 'GET',
		headers: {
			'Authorization': `Bearer ${bearerToken}`,
			'Content-Type': 'application/json'
		}
	})
		.then(response => response.json());

	let filter = genericReport.value.filter?.formatted ?? null;
	if (!filter && companyId) {
		filter = {
			and: [{
				val: {
					column: 'company_id',
					filter_type: 'numeric',
					operator: 'eq',
					value: companyId.toString()
				}
			}]
		};
	} else if (companyId) {
		filter = {
			and: [{
				val: {
					column: 'company_id',
					filter_type: 'numeric',
					operator: 'eq',
					value: companyId.toString()
				}
			}, filter]
		};
	}

	const body: GenericReport = {
		...genericReport.value,
		filter,
		// @ts-expect-error fuck this code (Types are bad)
		product_filter: getFinalyFilteredProducts(genericReport.value, products)
	};
	if (filterDates && filterDates.startDate && filterDates.endDate) {
		body.range = {
			begin: momentToNaiveDateTime(filterDates.startDate.toDate()),
			end: momentToNaiveDateTime(filterDates.endDate.toDate())
		};
	}
	else {
		// each bound resolves on its own — a preset through periodToDate, a
		// literal date parsed as such: routing both through whichever branch
		// `begin` fell into turned a fixed start with a 'Today' end into
		// new Date('Today'), an Invalid Date the query cannot carry
		const rangeBound = (value: string | undefined, begin: boolean): Date => {
			if (value && AllPeriods.includes(value)) return periodToDate(value, begin);
			if (value) {
				const d = new Date(value);
				if (begin) d.setHours(0, 0, 0, 0);
				return d;
			}
			if (begin) {
				const d = new Date(new Date().setMonth(new Date().getMonth() - 3, 1));
				d.setHours(0, 0, 0, 0);
				return d;
			}
			return new Date(new Date().setHours(23, 59, 59, 999));
		};
		body.range = {
			begin: momentToNaiveDateTime(rangeBound(genericReport.value.range?.begin, true)),
			end: momentToNaiveDateTime(rangeBound(genericReport.value.range?.end, false))
		};
	}
	return { report: await getGenericAndDecodeReport(body, userId, undefined, bearerToken), definition: genericReport };
}

function periodToDate(period: string, begin: boolean): Date {
	const now = new Date();

	if (begin) {
		switch (period) {
			case 'Today': {
				return new Date(now.getFullYear(), now.getMonth(), now.getDate());
			}
			case 'ThisWeek': {
				const dayOfWeek = now.getDay(); // Sunday = 0, Monday = 1, ...
				const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Monday as the start of the week
				const startOfWeek = new Date(now);
				startOfWeek.setDate(now.getDate() - diff);
				startOfWeek.setHours(0, 0, 0, 0);
				return startOfWeek;
			}
			case '7DaysAgo': {
				const date = new Date(now);
				date.setDate(now.getDate() - 7);
				date.setHours(0, 0, 0, 0);
				return date;
			}
			case 'LastWeek': {
				const dayOfWeek = now.getDay();
				const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
				const startOfLastWeek = new Date(now);
				startOfLastWeek.setDate(now.getDate() - diff - 7);
				startOfLastWeek.setHours(0, 0, 0, 0);
				return startOfLastWeek;
			}
			case 'ThisMonth': {
				return new Date(now.getFullYear(), now.getMonth(), 1);
			}
			case '30DaysAgo': {
				const date = new Date(now);
				date.setDate(now.getDate() - 30);
				date.setHours(0, 0, 0, 0);
				return date;
			}
			case 'LastMonth': {
				return new Date(now.getFullYear(), now.getMonth() - 1, 1);
			}
			case '90DaysAgo': {
				const date = new Date(now);
				date.setDate(now.getDate() - 90);
				date.setHours(0, 0, 0, 0);
				return date;
			}
			case 'ThisYear': {
				return new Date(now.getFullYear(), 0, 1);
			}
			case '365DaysAgo': {
				const date = new Date(now);
				date.setDate(now.getDate() - 365);
				date.setHours(0, 0, 0, 0);
				return date;
			}
			case 'LastYear': {
				return new Date(now.getFullYear() - 1, 0, 1);
			}
			default: {
				return new Date(now.getFullYear(), now.getMonth(), now.getDate());
			}
		}
	} else {
		switch (period) {
			case 'Today': {
				return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
			}
			case 'ThisWeek': {
				const dayOfWeek = now.getDay();
				const diff = dayOfWeek === 0 ? 0 : 7 - dayOfWeek; // Adjust for Sunday as the end of the week
				const endOfWeek = new Date(now);
				endOfWeek.setDate(now.getDate() + diff);
				endOfWeek.setHours(23, 59, 59, 999);
				return endOfWeek;
			}
			case '7DaysAgo': {
				const date = new Date(now);
				date.setDate(now.getDate() - 7);
				date.setHours(23, 59, 59, 999);
				return date;
			}
			case 'LastWeek': {
				const dayOfWeek = now.getDay();
				const diff = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
				const endOfLastWeek = new Date(now);
				endOfLastWeek.setDate(now.getDate() + diff - 7);
				endOfLastWeek.setHours(23, 59, 59, 999);
				return endOfLastWeek;
			}
			case 'ThisMonth': {
				return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
			}
			case '30DaysAgo': {
				const date = new Date(now);
				date.setDate(now.getDate() - 30);
				date.setHours(23, 59, 59, 999);
				return date;
			}
			case 'LastMonth': {
				return new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
			}
			case '90DaysAgo': {
				const date = new Date(now);
				date.setDate(now.getDate() - 90);
				date.setHours(23, 59, 59, 999);
				return date;
			}
			case 'ThisYear': {
				return new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
			}
			case '365DaysAgo': {
				const date = new Date(now);
				date.setDate(now.getDate() - 365);
				date.setHours(23, 59, 59, 999);
				return date;
			}
			case 'LastYear': {
				return new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
			}
			default: {
				return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
			}
		}
	}
}

const momentToNaiveDateTime = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based, so add 1
	const day = String(date.getDate()).padStart(2, '0');
	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');
	const seconds = String(date.getSeconds()).padStart(2, '0');

	return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};
